/**
 * Compact application row with pipeline for jobseeker dashboard home (desktop).
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const PIPELINE_STEPS = ['Applied', 'Review', 'Interview', 'Offer'];

const getPipelineIndex = (status) => {
  const value = (status || 'applied').toLowerCase().replace(/\s+/g, '_');
  if (['hired', 'accepted', 'offered'].includes(value)) return 3;
  if (value.includes('interview')) return 2;
  if (['shortlisted', 'reviewing', 'reviewed'].includes(value)) return 1;
  if (value === 'rejected') return -1;
  return 0;
};

const formatAppliedDate = (app) => {
  const raw = app.applied_date || app.applied_at || app.created_at;
  if (!raw) return 'Recently applied';
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return 'Recently applied';
    return `Applied ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  } catch {
    return 'Recently applied';
  }
};

const DashboardApplicationRow = ({ application, onView }) => {
  const title = application.job_title || application.title || 'Application';
  const company = application.company_name || application.company || '';
  const status = application.status || 'Applied';
  const pipelineIndex = getPipelineIndex(status);
  const isRejected = pipelineIndex === -1;

  return (
    <li
      className="dashboard-application-row"
      onClick={() => onView?.(application)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView?.(application);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="dashboard-application-row-main">
        <div className="dashboard-application-row-top">
          <div>
            <h4 className="dashboard-application-row-title">{title}</h4>
            {company && <p className="dashboard-application-row-company">{company}</p>}
          </div>
          <span className={`status-badge status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
            {status}
          </span>
        </div>

        <div className="dashboard-application-row-date">
          <FontAwesomeIcon icon={faCalendar} />
          <span>{formatAppliedDate(application)}</span>
        </div>

        {!isRejected ? (
          <div className="dashboard-application-pipeline" aria-label="Application progress">
            {PIPELINE_STEPS.map((step, index) => (
              <React.Fragment key={step}>
                <span
                  className={`dashboard-application-pipeline-step${
                    index <= pipelineIndex ? ' is-complete' : ''
                  }${index === pipelineIndex ? ' is-current' : ''}`}
                >
                  {step}
                </span>
                {index < PIPELINE_STEPS.length - 1 && (
                  <span
                    className={`dashboard-application-pipeline-line${
                      index < pipelineIndex ? ' is-complete' : ''
                    }`}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p className="dashboard-application-rejected">Application closed by employer</p>
        )}
      </div>

      <button
        type="button"
        className="dashboard-application-row-view"
        aria-label="View application"
        onClick={(e) => {
          e.stopPropagation();
          onView?.(application);
        }}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </li>
  );
};

export default DashboardApplicationRow;
